import { useTranslation } from 'react-i18next';
import type { EconomicsCompletionClaimNode } from '../../types/economicsTypes';
import { EconomicsCheckbox } from './EconomicsCheckbox';
import { formatDkk } from '../../utils/currencyUtils';
import { formatDate } from '../../utils/dateUtils';
import styles from './Economics.module.css';

interface EconomicsCompletionRowProps {
  node: EconomicsCompletionClaimNode;
  patientName: string;
  canWrite: boolean;
  onToggleClaim: (key: string, claimed: boolean) => void;
}

// The completion-reimbursement claim for one patient on one plan. It can only be claimed once
// the patient has completed every group on the plan; an existing claim can always be removed.
export const EconomicsCompletionRow = ({
  node,
  patientName,
  canWrite,
  onToggleClaim
}: EconomicsCompletionRowProps) => {
  const { t, i18n } = useTranslation();
  const isLocked = !node.claimed && !node.isClaimable;

  let dateLabel = '—';
  if (node.isUnsaved) dateLabel = t('economics-unsaved');
  else if (node.claimedAt) dateLabel = formatDate(node.claimedAt, i18n.language);

  return (
    <div
      className={`${styles.row} ${styles.rowGroup}`}
      title={isLocked ? t('economics-completion-locked') : undefined}
    >
      <span className={styles.spacer} />
      <EconomicsCheckbox
        state={node.claimed ? 'all' : 'none'}
        onChange={(next) => onToggleClaim(node.key, next)}
        disabled={!canWrite || isLocked}
        ariaLabel={t('economics-claim-completion', { patient: patientName })}
      />
      <div className={styles.label}>
        <span>{t('economics-completion-label')}</span>
      </div>
      <div className={styles.spread}>
        <span className={styles.date}>{dateLabel}</span>
        <div className={styles.amountCell}>
          <span className={`${styles.amount} ${node.claimed ? '' : styles.amountMuted}`}>
            {formatDkk(node.amount, i18n.language)}
          </span>
        </div>
      </div>
    </div>
  );
};
